import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Plus, Trash2, Eye, Edit2, X, Map } from 'lucide-react';
import {
  fetchZones,
  fetchZonesCount,
  createZone,
  updateZone,
  deleteZone,
  fetchCountriesForDropdown,
  fetchStatesByCountry,
  fetchSuperUsers,
} from '../services/api.ts';
import './Zones.css';

interface CountryOption { id: number; name: string; code: string; }
interface StateOption { id: number; name: string; }
interface SuperUserOption { id: number; username: string; email: string; }

interface FormData {
  name: string;
  description: string;
  country_id: number | '';
  state_id: number | '';
  manager_id: number | '';
  delivery_fee: string;
  surcharge_percentage: string;
}

const emptyForm: FormData = { name: '', description: '', country_id: '', state_id: '', manager_id: '', delivery_fee: '0', surcharge_percentage: '0' };

const Zones: React.FC = () => {
  const navigate = useNavigate();
  const [zones, setZones] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  const [showModal, setShowModal] = useState(false);
  const [editingZone, setEditingZone] = useState<any | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [states, setStates] = useState<StateOption[]>([]);
  const [superUsers, setSuperUsers] = useState<SuperUserOption[]>([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * perPage;
      const [list, countRes] = await Promise.all([
        fetchZones(offset, perPage),
        fetchZonesCount(),
      ]);
      setZones(list);
      setTotal(countRes.count);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load zones');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    fetchCountriesForDropdown().then(setCountries).catch(console.error);
    fetchSuperUsers().then((data: any) => setSuperUsers(data)).catch(console.error);
  }, []);

  const filtered = zones.filter((z) => {
    const s = `${z.name} ${z.description || ''} ${z.manager?.name || ''} ${z.state?.name || ''}`.toLowerCase();
    return s.includes(searchTerm.toLowerCase());
  });

  const totalPages = Math.ceil(total / perPage);

  const openAdd = () => {
    setEditingZone(null);
    setForm(emptyForm);
    setStates([]);
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (z: any) => {
    setEditingZone(z);
    if (z.country?.id) {
      fetchStatesByCountry(z.country.id).then(setStates).catch(console.error);
    }
    setForm({
      name: z.name,
      description: z.description || '',
      country_id: z.country?.id || '',
      state_id: z.state?.id || '',
      manager_id: z.manager?.id || '',
      delivery_fee: z.delivery_fee != null ? String(z.delivery_fee) : '0',
      surcharge_percentage: z.surcharge_percentage != null ? String(z.surcharge_percentage) : '0',
    });
    setFormError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Zone name is required'); return; }
    if (!form.country_id) { setFormError('Country is required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      const payload: any = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        country_id: Number(form.country_id),
        state_id: form.state_id ? Number(form.state_id) : undefined,
        manager_id: form.manager_id ? Number(form.manager_id) : undefined,
        delivery_fee: form.delivery_fee ? parseFloat(form.delivery_fee) : 0,
        surcharge_percentage: form.surcharge_percentage ? parseFloat(form.surcharge_percentage) : 0,
      };
      if (editingZone) {
        await updateZone(editingZone.id, payload);
      } else {
        await createZone(payload);
      }
      setShowModal(false);
      loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to save zone');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this zone?')) return;
    try {
      await deleteZone(id);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete zone');
    }
  };

  if (loading) {
    return (
      <div className="zones-page">
        <div className="zones-loading"><div className="zones-spinner" /><p>Loading zones...</p></div>
      </div>
    );
  }

  return (
    <div className="zones-page">
      <div className="zones-header">
        <div className="zones-header-left">
          <Map size={22} />
          <h1>Zones</h1>
          <span className="zones-count">{total}</span>
        </div>
        <button className="zones-btn zones-btn-primary" onClick={openAdd}>
          <Plus size={16} /> Add Zone
        </button>
      </div>

      <div className="zones-search-bar">
        <Search size={18} />
        <input type="text" placeholder="Search by name, description, manager, state..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>

      {error && <div className="zones-error">{error}</div>}

      <div className="zones-table-wrap">
        <table className="zones-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Description</th>
              <th>Delivery Fee</th>
              <th>Surcharge</th>
              <th>State</th>
              <th>Manager</th>
              <th>Postcodes</th>
              <th>Drivers</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={11} className="zones-empty">No zones found</td></tr>
            ) : filtered.map((z) => (
              <tr key={z.id}>
                <td>{z.id}</td>
                <td className="zones-cell-name">{z.name}</td>
                <td>{z.description || '—'}</td>                <td>{z.delivery_fee > 0 ? `$${Number(z.delivery_fee).toFixed(2)}` : <span className="zones-badge zones-badge-success">Free</span>}</td>                <td>{z.surcharge_percentage > 0 ? `${Number(z.surcharge_percentage).toFixed(2)}%` : <span className="zones-badge zones-badge-success">None</span>}</td>                <td>{z.state?.name || '—'}</td>
                <td>{z.manager?.name || z.manager?.username || '—'}</td>
                <td>{z.postcode_count ?? 0}</td>
                <td>{z.driver_count ?? 0}</td>
                <td>
                  {z.is_active ? (
                    <span className="zones-badge zones-badge-success">Active</span>
                  ) : (
                    <span className="zones-badge zones-badge-danger">Inactive</span>
                  )}
                </td>
                <td>
                  <div className="zones-cell-actions">
                    <button className="zones-action-btn zones-action-view" title="View" onClick={() => navigate(`/dashboard/zones/${z.id}`)}>
                      <Eye size={15} />
                    </button>
                    <button className="zones-action-btn zones-action-edit" title="Edit" onClick={() => openEdit(z)}>
                      <Edit2 size={15} />
                    </button>
                    <button className="zones-action-btn zones-action-delete" title="Delete" onClick={() => handleDelete(z.id)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="zones-pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {showModal && (
        <div className="zones-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="zones-modal" onClick={e => e.stopPropagation()}>
            <div className="zones-modal-header">
              <h2>{editingZone ? 'Edit Zone' : 'Create Zone'}</h2>
              <button className="zones-modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="zones-modal-body">
              {formError && <div className="zones-form-error">{formError}</div>}
              <div className="zones-form-row">
                <label>Name *</label>
                <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Melbourne CBD" />
              </div>
              <div className="zones-form-row">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} placeholder="Optional description" />
              </div>
              <div className="zones-form-row">
                <label>Country *</label>
                <select value={form.country_id} onChange={e => {
                  const cid = e.target.value ? Number(e.target.value) : '';
                  setForm({...form, country_id: cid, state_id: ''});
                  if (cid) { fetchStatesByCountry(Number(cid)).then(setStates).catch(console.error); } else { setStates([]); }
                }}>
                  <option value="">Select country</option>
                  {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="zones-form-row">
                <label>State</label>
                <select value={form.state_id} onChange={e => setForm({...form, state_id: e.target.value ? Number(e.target.value) : ''})}>
                  <option value="">Select state (optional)</option>
                  {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="zones-form-row">
                <label>Zone Manager</label>
                <select value={form.manager_id} onChange={e => setForm({...form, manager_id: e.target.value ? Number(e.target.value) : ''})}>
                  <option value="">No manager</option>
                  {superUsers.map((su: any) => <option key={su.id} value={su.id}>{su.username} ({su.email})</option>)}
                </select>
              </div>
              <div className="zones-form-row">
                <label>Delivery Fee ($)</label>
                <input type="number" min="0" step="0.01" value={form.delivery_fee} onChange={e => setForm({...form, delivery_fee: e.target.value})} placeholder="0.00 = Free delivery" />
                <small style={{ color: '#888', marginTop: '4px' }}>Set to 0 for free delivery</small>
              </div>
              <div className="zones-form-row">
                <label>Surcharge (%)</label>
                <input type="number" min="0" max="100" step="0.01" value={form.surcharge_percentage} onChange={e => setForm({...form, surcharge_percentage: e.target.value})} placeholder="0.00 = No surcharge" />
                <small style={{ color: '#888', marginTop: '4px' }}>Percentage applied to item total (0 = no surcharge)</small>
              </div>
            </div>
            <div className="zones-modal-footer">
              <button className="zones-btn zones-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="zones-btn zones-btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Zones;
